import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { tenantApi, SupportChatResponse, SupportConversationSummary } from "@/lib/api/client";
import { Button, ActionIcon, TextInput, Text, Popover, Stack, Group, Box, Loader } from "@mantine/core";
import { MessageCircle, Send, Plus, X, ArrowLeft, ChevronRight, Maximize2, Archive, XCircle } from "lucide-react";

interface ChatMessage {
  role: "user" | "assistant";
  content: string;
}

type View = "list" | "chat";

const SUGGESTED = ["Find a meeting room", "Search the Office Guide", "Who are my colleagues?"];

export function ChatPopover({ currentPath }: { currentPath?: string }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<View>("list");
  const [conversations, setConversations] = useState<SupportConversationSummary[]>([]);
  const [loadingList, setLoadingList] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [conversationId, setConversationId] = useState<number | undefined>();
  const [conversationStatus, setConversationStatus] = useState<string>("open");
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      loadConversations();
    }
  }, [open]);

  useEffect(() => {
    if (view === "chat") {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [view]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  async function loadConversations() {
    setLoadingList(true);
    try {
      const res = await tenantApi.listSupportConversations();
      setConversations(res.data);
    } catch {
      // silently fail
    } finally {
      setLoadingList(false);
    }
  }

  async function openConversation(id: number) {
    setIsLoading(true);
    setView("chat");
    try {
      const res = await tenantApi.getSupportConversation(id);
      setConversationId(id);
      setConversationStatus(res.data.status);
      setMessages(res.data.history.map((m) => ({ role: m.role as "user" | "assistant", content: m.content })));
    } catch {
      setMessages([{ role: "assistant", content: "Failed to load conversation." }]);
    } finally {
      setIsLoading(false);
    }
  }

  function startNewChat() {
    setMessages([]);
    setConversationId(undefined);
    setConversationStatus("open");
    setInput("");
    setView("chat");
  }

  function backToList() {
    setView("list");
    loadConversations();
  }

  async function archiveConversation() {
    if (!conversationId) return;
    await tenantApi.patchSupportConversation(conversationId, "archived");
    backToList();
  }

  async function closeConversation() {
    if (!conversationId) return;
    await tenantApi.patchSupportConversation(conversationId, "closed");
    backToList();
  }

  // There is no member DELETE endpoint — "remove from my list" archives the
  // conversation (the list is fetched with include_archived=false).
  async function archiveFromList(id: number, e: React.MouseEvent) {
    e.stopPropagation();
    setDeletingId(id);
    try {
      await tenantApi.patchSupportConversation(id, "archived");
      setConversations((prev) => prev.filter((c) => c.id !== id));
    } finally {
      setDeletingId(null);
    }
  }

  async function sendMessage(text?: string) {
    const msg = (text ?? input).trim();
    if (!msg || isLoading) return;

    setInput("");
    setMessages((prev) => [...prev, { role: "user", content: msg }]);
    setIsLoading(true);

    const context = currentPath ? `User is currently on page: ${currentPath}` : undefined;

    try {
      const res = await tenantApi.supportChat(msg, conversationId, context);
      const data: SupportChatResponse = res.data;
      setConversationId(data.conversation_id);
      setMessages(data.history.map((m) => ({ role: m.role as "user" | "assistant", content: m.content })));
    } catch {
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: "Sorry, something went wrong. Please try again." },
      ]);
    } finally {
      setIsLoading(false);
    }
  }

  function formatDate(iso: string) {
    const d = new Date(iso);
    const now = new Date();
    const diff = now.getTime() - d.getTime();
    if (diff < 86_400_000) return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false });
    if (diff < 7 * 86_400_000) return d.toLocaleDateString([], { weekday: "short" });
    return d.toLocaleDateString([], { month: "short", day: "numeric" });
  }

  return (
    <Popover opened={open} onChange={setOpen} position="bottom-end" offset={8}>
      <Popover.Target>
        <ActionIcon variant="subtle" aria-label="Chat" onClick={() => setOpen((o) => !o)}>
          <MessageCircle size={16} />
        </ActionIcon>
      </Popover.Target>
      <Popover.Dropdown
        p={0}
        w="min(384px, 95vw)" h={520} style={{ display: "flex", flexDirection: "column", overflow: "hidden" }}
      >
        <style>{`
          .chat-conversation-row:hover { background: var(--mantine-color-default-hover); }
          .chat-delete-btn { opacity: 0; transition: opacity 150ms; }
          .chat-conversation-row:hover .chat-delete-btn { opacity: 1; }
        `}</style>
        {/* -- List view -- */}
        {view === "list" && (
          <>
            <Group justify="space-between" px="sm" py="xs" style={{ borderBottom: "1px solid var(--mantine-color-default-border)", flexShrink: 0 }}>
              <Text fw={500} size="sm">Support Chat</Text>
              <Group gap={4}>
                <ActionIcon variant="subtle" size="sm" onClick={startNewChat} aria-label="New chat">
                  <Plus size={12} />
                </ActionIcon>
                <ActionIcon variant="subtle" size="sm" onClick={() => setOpen(false)} aria-label="Close">
                  <X size={12} />
                </ActionIcon>
              </Group>
            </Group>

            <Box style={{ flex: 1, overflowY: "auto" }}>
              {loadingList && (
                <Group justify="center" py="xl" c="dimmed">
                  <Loader size={16} />
                  <Text size="sm">Loading...</Text>
                </Group>
              )}

              {!loadingList && conversations.length === 0 && (
                <Stack align="center" justify="center" h="100%" gap="sm" c="dimmed" px="md" ta="center">
                  <MessageCircle size={32} style={{ opacity: 0.3 }} />
                  <Text size="sm">No conversations yet.</Text>
                  <Button size="xs" onClick={startNewChat} leftSection={<Plus size={16} />}>
                    Start a chat
                  </Button>
                </Stack>
              )}

              {!loadingList && conversations.map((conv) => (
                <Group
                  key={conv.id}
                  gap="xs"
                  px="sm"
                  py="xs"
                  wrap="nowrap"
                  style={{ borderBottom: "1px solid var(--mantine-color-default-border)", cursor: "pointer" }}
                  className="chat-conversation-row"
                  onClick={() => openConversation(conv.id)}
                >
                  <Box miw={0} flex={1}>
                    <Text size="sm" truncate>{conv.preview}</Text>
                    <Text size="xs" c="dimmed" mt={2}>
                      {formatDate(conv.created_at)} · {conv.message_count} messages
                    </Text>
                  </Box>
                  <Group gap={4} style={{ flexShrink: 0 }}>
                    <ActionIcon
                      variant="subtle"
                      size="sm"
                      className="chat-delete-btn"
                      onClick={(e) => archiveFromList(conv.id, e)}
                      disabled={deletingId === conv.id}
                      aria-label="Archive"
                    >
                      {deletingId === conv.id
                        ? <Loader size={12} />
                        : <Archive size={12} color="var(--mantine-color-dimmed)" />}
                    </ActionIcon>
                    <ChevronRight size={16} color="var(--mantine-color-dimmed)" />
                  </Group>
                </Group>
              ))}
            </Box>
          </>
        )}

        {/* -- Chat view -- */}
        {view === "chat" && (
          <>
            <Group gap={4} px="sm" py="xs" style={{ borderBottom: "1px solid var(--mantine-color-default-border)", flexShrink: 0 }}>
              <ActionIcon variant="subtle" size="sm" onClick={backToList} aria-label="Back">
                <ArrowLeft size={12} />
              </ActionIcon>
              <Text fw={500} size="sm" flex={1}>
                {conversationId ? "Conversation" : "New Chat"}
              </Text>
              {conversationId && conversationStatus === "open" && (
                <>
                  <ActionIcon variant="subtle" size="sm" onClick={archiveConversation} aria-label="Archive">
                    <Archive size={12} />
                  </ActionIcon>
                  <ActionIcon variant="subtle" size="sm" onClick={closeConversation} aria-label="Close conversation">
                    <XCircle size={12} />
                  </ActionIcon>
                </>
              )}
              {conversationId && (
                <ActionIcon
                  variant="subtle" size="sm"
                  onClick={() => { setOpen(false); navigate(`/support/${conversationId}`); }}
                  aria-label="Open fullscreen"
                >
                  <Maximize2 size={12} />
                </ActionIcon>
              )}
              <ActionIcon variant="subtle" size="sm" onClick={() => setOpen(false)} aria-label="Close">
                <X size={12} />
              </ActionIcon>
            </Group>

            <Stack gap="sm" p="sm" style={{ flex: 1, overflowY: "auto" }}>
              {messages.length === 0 && !isLoading && (
                <Stack align="center" justify="center" h="100%" gap="sm" c="dimmed">
                  <Text size="sm">What can I help you with?</Text>
                  <Stack gap={6} w="100%">
                    {SUGGESTED.map((prompt) => (
                      <Button
                        key={prompt}
                        variant="default"
                        size="xs"
                        justify="flex-start"
                        onClick={() => sendMessage(prompt)}
                        styles={{ label: { fontSize: "var(--mantine-font-size-xs)" } }}
                      >
                        {prompt}
                      </Button>
                    ))}
                  </Stack>
                </Stack>
              )}

              {messages.map((msg, i) => (
                <Box key={i} display="flex" style={{ justifyContent: msg.role === "user" ? "flex-end" : "flex-start" }}>
                  <Box
                    style={{
                      borderRadius: "var(--mantine-radius-xl)",
                      padding: "8px 12px",
                      maxWidth: "85%",
                      fontSize: "var(--mantine-font-size-sm)",
                      // Dark brand shade + white text so contrast clears WCAG
                      // 2.1 AA / EN 301 549 (EAA) for any customer brand colour.
                      backgroundColor: msg.role === "user"
                        ? "var(--mantine-color-brand-8)"
                        : "var(--mantine-color-default-hover)",
                      color: msg.role === "user"
                        ? "var(--mantine-color-white)"
                        : "var(--mantine-color-text)",
                    }}
                  >
                    {msg.role === "assistant" ? (
                      <Box fz="sm" maw="none" style={{ overflow: "hidden" }}>
                        <ReactMarkdown
                          remarkPlugins={[remarkGfm]}
                          components={{ img: ({ node, ...props }) => <img {...props} style={{ maxWidth: "100%", height: "auto", borderRadius: 4 }} /> }}
                        >
                          {msg.content}
                        </ReactMarkdown>
                      </Box>
                    ) : (
                      msg.content
                    )}
                  </Box>
                </Box>
              ))}

              {isLoading && (
                <Box display="flex" style={{ justifyContent: "flex-start" }}>
                  <Group
                    gap={6}
                    style={{
                      borderRadius: "var(--mantine-radius-xl)",
                      padding: "8px 12px",
                      backgroundColor: "var(--mantine-color-default-hover)",
                    }}
                  >
                    <Loader size={12} />
                    <Text size="xs" c="dimmed">Thinking...</Text>
                  </Group>
                </Box>
              )}

              <div ref={bottomRef} />
            </Stack>

            <Group gap="xs" p="xs" style={{ borderTop: "1px solid var(--mantine-color-default-border)", flexShrink: 0 }}>
              <TextInput
                ref={inputRef}
                placeholder="Ask a question..."
                value={input}
                onChange={(e) => setInput(e.currentTarget.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    sendMessage();
                  }
                }}
                disabled={isLoading}
                size="sm"
                flex={1}
              />
              <ActionIcon size="lg" onClick={() => sendMessage()} disabled={isLoading || !input.trim()}>
                {isLoading ? <Loader size={16} /> : <Send size={16} />}
              </ActionIcon>
            </Group>
          </>
        )}
      </Popover.Dropdown>
    </Popover>
  );
}
